// Weapon SVG imports
import glock from '../../../content/weapons/glock.svg';
import usp_silencer from '../../../content/weapons/usp_silencer.svg';
import hkp2000 from '../../../content/weapons/hkp2000.svg';
import p250 from '../../../content/weapons/p250.svg';
import fiveseven from '../../../content/weapons/fiveseven.svg';
import tec9 from '../../../content/weapons/tec9.svg';
import cz75a from '../../../content/weapons/cz75a.svg';
import deagle from '../../../content/weapons/deagle.svg';
import revolver from '../../../content/weapons/revolver.svg';
import elite from '../../../content/weapons/elite.svg';
import mac10 from '../../../content/weapons/mac10.svg';
import mp9 from '../../../content/weapons/mp9.svg';
import mp7 from '../../../content/weapons/mp7.svg';
import mp5sd from '../../../content/weapons/mp5sd.svg';
import ump45 from '../../../content/weapons/ump45.svg';
import p90 from '../../../content/weapons/p90.svg';
import bizon from '../../../content/weapons/bizon.svg';
import galilar from '../../../content/weapons/galilar.svg';
import famas from '../../../content/weapons/famas.svg';
import ak47 from '../../../content/weapons/ak47.svg';
import m4a1 from '../../../content/weapons/m4a1.svg';
import m4a1_silencer from '../../../content/weapons/m4a1_silencer.svg';
import sg556 from '../../../content/weapons/sg556.svg';
import aug from '../../../content/weapons/aug.svg';
import ssg08 from '../../../content/weapons/ssg08.svg';
import awp from '../../../content/weapons/awp.svg';
import scar20 from '../../../content/weapons/scar20.svg';
import g3sg1 from '../../../content/weapons/g3sg1.svg';
import nova from '../../../content/weapons/nova.svg';
import xm1014 from '../../../content/weapons/xm1014.svg';
import sawedoff from '../../../content/weapons/sawedoff.svg';
import mag7 from '../../../content/weapons/mag7.svg';
import negev from '../../../content/weapons/negev.svg';
import m249 from '../../../content/weapons/m249.svg';
import knife from '../../../content/weapons/knife.svg';
import taser from '../../../content/weapons/taser.svg';
import c4 from '../../../content/weapons/c4.svg';
import flashbang from '../../../content/weapons/flashbang.svg';
import hegrenade from '../../../content/weapons/hegrenade.svg';
import smokegrenade from '../../../content/weapons/smokegrenade.svg';
import molotov from '../../../content/weapons/molotov.svg';
import incgrenade from '../../../content/weapons/incgrenade.svg';
import decoy from '../../../content/weapons/decoy.svg';

// Equipment imports
import defuseKit from '../../../content/weapons/defuse_kit.svg';

// Armor imports
import armorNone from '../../../content/armor/icon_armor_none_default.svg';
import armorVest from '../../../content/armor/icon_armor_full_default.svg';
import armorHelmet from '../../../content/armor/icon_armor_helmet_default.svg';

// Kill indicator imports
import headshot from '../../../content/killIndicators/headshot.svg';
import wallbang from '../../../content/killIndicators/wallbang.svg';
import noscope from '../../../content/killIndicators/noscope.svg';
import smokeKill from '../../../content/killIndicators/smoke_kill.svg';
import skull from '../../../content/killIndicators/icon_skull_default.svg';

// Weapon byte ID → SVG URL (matches WEAPON_NAMES in replayParser.ts)
export const WEAPON_ICON_URLS: Record<number, string> = {
    1: glock, 2: usp_silencer, 3: hkp2000, 4: p250, 5: fiveseven,
    6: tec9, 7: cz75a, 8: deagle, 9: revolver, 10: elite,
    11: mac10, 12: mp9, 13: mp7, 14: mp5sd, 15: ump45, 16: p90, 17: bizon,
    20: galilar, 21: famas, 22: ak47, 23: m4a1, 24: m4a1_silencer,
    25: sg556, 26: aug,
    30: ssg08, 31: awp, 32: scar20, 33: g3sg1,
    40: nova, 41: xm1014, 42: sawedoff, 43: mag7,
    50: negev, 51: m249,
    60: knife, 61: taser,
    70: hegrenade, 71: flashbang, 72: smokegrenade, 73: molotov, 74: incgrenade, 75: decoy,
    80: c4,
};

export const KILL_INDICATOR_URLS = {
    headshot,
    wallbang,
    noscope,
    smokeKill,
    skull,
} as const;

export const UTIL_ICON_URLS = {
    flash: flashbang,
    smoke: smokegrenade,
    he: hegrenade,
    fire: molotov,
} as const;

export const EQUIPMENT_URLS = {
    defuseKit,
} as const;

export const ARMOR_ICON_URLS = {
    none: armorNone,
    vest: armorVest,
    helmet: armorHelmet,
} as const;

export interface PreloadedIcons {
    weapons: Record<number, HTMLImageElement>;
    indicators: Record<string, HTMLImageElement>;
    equipment: Record<string, HTMLImageElement>;
}

/** Convert a URL to a data URI by fetching it once. Falls back to original URL on error. */
async function toDataUri(url: string): Promise<string> {
    try {
        const res = await fetch(url);
        const blob = await res.blob();
        return await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => resolve(url);
            reader.readAsDataURL(blob);
        });
    } catch {
        return url;
    }
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => {
            console.warn(`[ReplayIcons] Failed to load: ${src}`);
            resolve(null);
        };
        img.src = src;
    });
}

/** Cached data-URI versions of all icon URLs. Once resolved, <img src> uses these inline — zero network. */
let _cachedDataUris: Promise<void> | null = null;
const _weaponDataUris: Record<number, string> = {};
const _indicatorDataUris: Record<string, string> = {};
const _equipmentDataUris: Record<string, string> = {};
const _armorDataUris: Record<string, string> = {};
const _utilDataUris: Record<string, string> = {};

function ensureDataUris(): Promise<void> {
    if (_cachedDataUris) return _cachedDataUris;
    _cachedDataUris = (async () => {
        const tasks: Promise<void>[] = [];
        for (const [id, url] of Object.entries(WEAPON_ICON_URLS)) {
            tasks.push(toDataUri(url).then(d => { _weaponDataUris[Number(id)] = d; }));
        }
        for (const [key, url] of Object.entries(KILL_INDICATOR_URLS)) {
            tasks.push(toDataUri(url).then(d => { _indicatorDataUris[key] = d; }));
        }
        for (const [key, url] of Object.entries(EQUIPMENT_URLS)) {
            tasks.push(toDataUri(url).then(d => { _equipmentDataUris[key] = d; }));
        }
        for (const [key, url] of Object.entries(ARMOR_ICON_URLS)) {
            tasks.push(toDataUri(url).then(d => { _armorDataUris[key] = d; }));
        }
        for (const [key, url] of Object.entries(UTIL_ICON_URLS)) {
            tasks.push(toDataUri(url).then(d => { _utilDataUris[key] = d; }));
        }
        await Promise.all(tasks);
        console.log(`[ReplayIcons] Converted ${Object.keys(_weaponDataUris).length} weapons + indicators + equipment to data URIs`);
    })();
    return _cachedDataUris;
}

/** Get a weapon icon as data URI (or original URL if not yet cached). */
export function getWeaponDataUri(id: number): string | undefined {
    return _weaponDataUris[id] || WEAPON_ICON_URLS[id];
}
export function getIndicatorDataUri(key: string): string | undefined {
    return _indicatorDataUris[key] || (KILL_INDICATOR_URLS as Record<string, string>)[key];
}
export function getEquipmentDataUri(key: string): string | undefined {
    return _equipmentDataUris[key] || (EQUIPMENT_URLS as Record<string, string>)[key];
}
export function getArmorDataUri(key: string): string {
    return _armorDataUris[key] || (ARMOR_ICON_URLS as Record<string, string>)[key] || '';
}
export function getUtilDataUri(key: string): string {
    return _utilDataUris[key] || (UTIL_ICON_URLS as Record<string, string>)[key] || '';
}

let _cachedIcons: Promise<PreloadedIcons> | null = null;

export function preloadIcons(): Promise<PreloadedIcons> {
    if (_cachedIcons) return _cachedIcons;

    _cachedIcons = (async () => {
        // Convert all URLs to data URIs first
        await ensureDataUris();

        const weapons: Record<number, HTMLImageElement> = {};
        const indicators: Record<string, HTMLImageElement> = {};
        const equipment: Record<string, HTMLImageElement> = {};

        // Use data URIs for canvas HTMLImageElement preloading too
        const weaponEntries = Object.entries(_weaponDataUris).map(async ([id, dataUri]) => {
            const img = await loadImage(dataUri);
            if (img) weapons[Number(id)] = img;
        });

        const indicatorEntries = Object.entries(_indicatorDataUris).map(async ([key, dataUri]) => {
            const img = await loadImage(dataUri);
            if (img) indicators[key] = img;
        });

        const equipmentEntries = Object.entries(_equipmentDataUris).map(async ([key, dataUri]) => {
            const img = await loadImage(dataUri);
            if (img) equipment[key] = img;
        });

        await Promise.all([...weaponEntries, ...indicatorEntries, ...equipmentEntries]);
        console.log(`[ReplayIcons] Loaded ${Object.keys(weapons).length} weapons, ${Object.keys(indicators).length} indicators, ${Object.keys(equipment).length} equipment`);
        return { weapons, indicators, equipment };
    })();

    return _cachedIcons;
}
